import { createSelector } from '@reduxjs/toolkit';
import { DEFAULT_STATUS_FOR_ARCHIVED_SEARCH, DEFAULT_STATUS_FOR_ONLINE_SEARCH } from '@/utils/queryParams';
import {
    DELETE_SAVE_SEARCH_FAIL,
    DELETE_SAVE_SEARCH_REQUEST,
    DELETE_SAVE_SEARCH_SUCCESS,
    LOAD_SAVED_SEARCHES_SUCCESS,
    LOG_OUT_BIDDER,
    SAVE_SEARCH_FAIL,
    SAVE_SEARCH_REQUEST,
    SAVE_SEARCH_SUCCESS,
} from './actions';
import { fetchSavedSearchesIfNeeded } from './savedSearch';
import { getAuthToken } from './user';
import { getDeployment } from './config';
import { getFollowSearchAnalytics, getUnfollowSearchAnalytics } from './analytics';
import { getSavedSearchIdByKey } from './saveSearchCheck';
import { GlobalState } from '../rootReducer';
import { SearchParams } from '@/types/SaveSearch';
import { toFacetUrlParams } from './savedSearchFacets';
import api from '@/redux/api/saveSearch';
import cloneDeep from 'lodash/cloneDeep';

// reducer
export const DEFAULT_STATE = {
    byParams: {},
    error: false,
    //TODO remove these and update places using them to use byParams
    submitted: false,
    success: false,
};

type SavedSearch = {
    deleted?: boolean;
    key: string;
    savedSearchId?: number;
    submitted: boolean;
    success: boolean;
};

type ByParamsType = {
    [key: string]: SavedSearch;
};

export type State = {
    byParams: ByParamsType;
    error: boolean;
    submitted: boolean;
    success: boolean;
};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    const existing = cloneDeep(state.byParams);
    switch (action.type) {
        case SAVE_SEARCH_REQUEST:
        case DELETE_SAVE_SEARCH_REQUEST:
            return {
                ...state,
                byParams: {
                    ...existing,
                    [`${action.payload}`]: {
                        ...existing[`${action.payload}`],
                        submitted: true,
                        success: false,
                    },
                },
                submitted: true,
                success: false,
            };
        case SAVE_SEARCH_SUCCESS:
            return {
                ...state,
                byParams: {
                    ...existing,
                    [`${action.payload.key}`]: {
                        ...existing[`${action.payload.key}`],
                        deleted: false,
                        key: `${action.payload.key}`,
                        savedSearchId: action.payload.id,
                    },
                },
                submitted: false,
                success: true,
            };
        case LOAD_SAVED_SEARCHES_SUCCESS:
            let byParams = {};
            action.payload?.forEach((search) => {
                const key = createSaveSearchKeyBySearchParams(toFacetUrlParams(search.facets));
                if (key) {
                    byParams = {
                        ...byParams,
                        [key]: {
                            deleted: false,
                            key,
                            savedSearchId: search.savedSearchId,
                            submitted: false,
                            success: true,
                        },
                    };
                }
            });
            return { ...state, byParams: { ...existing, ...byParams } };
        case LOG_OUT_BIDDER:
            return { ...DEFAULT_STATE };
        case DELETE_SAVE_SEARCH_SUCCESS:
            return {
                ...state,
                byParams: {
                    ...existing,
                    [`${action.payload}`]: {
                        ...existing[`${action.payload}`],
                        deleted: true,
                    },
                },
                error: false,
                submitted: false,
                success: true,
            };
        case SAVE_SEARCH_FAIL:
            return {
                ...state,
                byParams: {
                    ...existing,
                    [`${action.meta.key}`]: {
                        ...existing[`${action.meta.key}`],
                        submitted: false,
                        success: false,
                    },
                },
                error: true,
                submitted: false,
                success: false,
            };
        default:
            return state;
    }
}

/* SELECTORS */
export const getSaveSearchState = (state: GlobalState) => state.saveSearch;

const idSelector = (state: GlobalState, id: number | string) => id;

const byParamsSelector = createSelector(getSaveSearchState, (state) => state.byParams);

export const getSaveSearchSubmitted = createSelector(getSaveSearchState, (state) => state.submitted);

export const getSaveSearchParams = (queryParams: any, pathname: string = '') => {
    const newParams = cloneDeep(queryParams);

    if (newParams.status === DEFAULT_STATUS_FOR_ARCHIVED_SEARCH) {
        newParams.status = DEFAULT_STATUS_FOR_ONLINE_SEARCH;
    }

    delete newParams.pageSize;
    delete newParams.pagesize;
    delete newParams.page;
    delete newParams.sort;
    delete newParams.saveSearch;

    newParams.keyword = newParams.keyword ? newParams.keyword : '';

    if (pathname.includes('/c/') && !newParams.campaign_id) {
        let pathCategoryIds: any = pathname.match(/\d+/g) || [];
        newParams.category_id = [...pathCategoryIds, newParams.category_id]
            .filter((i) => Boolean(i))
            .sort()
            .join(',');
    }

    return newParams;
};

export const createSaveSearchParams = (categoryId: string) => {
    return {
        category_id: categoryId,
    };
};

export const createSaveSearchKey = (categoryId: string) => {
    return createSaveSearchKeyBySearchParams(createSaveSearchParams(categoryId));
};

export const createSaveSearchKeyBySearchParams = (searchParams: SearchParams): string => {
    if (!searchParams) {
        return '';
    }
    const sortedParams = {};
    Object.keys(searchParams)
        .sort()
        .forEach((key) => {
            // replace all wierd whitespace with standard spaces, and trim the ends
            sortedParams[key] =
                (typeof searchParams?.[key] === 'string' ? searchParams[key] : '').replace(/\s+/g, ' ').trim() ||
                sortedParams[key];
        });
    let key = JSON.stringify(sortedParams);
    key = key.replace(/}|{|"/g, '');
    return key;
};

export const getHasSavedSearches = createSelector(byParamsSelector, (byParams) => {
    const savedSearches = Object.keys(byParams).filter((key) => {
        return !byParams[key].deleted;
    });
    return Boolean(savedSearches.length);
});

export const getIsSavedSearch = createSelector([byParamsSelector, idSelector], (byParams, saveSearchKey) => {
    const savedSearch = Boolean(byParams[saveSearchKey] && !byParams[saveSearchKey].deleted);
    return savedSearch;
});

export const getSavedSearchKeyById = createSelector([byParamsSelector, idSelector], (byParams, id) => {
    const savedSearchArray = Object.keys(byParams);
    let savedSearchKey = '';
    savedSearchArray.forEach((key) => {
        if (byParams[key].savedSearchId === id) {
            savedSearchKey = byParams[key].key;
        }
    });

    return savedSearchKey;
});

/* ACTION CREATORS */
export const submitSaveSearch =
    (ssParams: SearchParams, sendAlert: boolean) => async (dispatch: Function, getState: () => GlobalState) => {
        try {
            const state = getState();
            const authToken = getAuthToken(state);
            const deployment = getDeployment(state);
            const analytics = getFollowSearchAnalytics();
            const savedSearchKey = createSaveSearchKeyBySearchParams(ssParams);

            dispatch({
                payload: savedSearchKey,
                type: SAVE_SEARCH_REQUEST,
            });
            const { payload } = await api.postSaveSearch({ authToken, deployment, sendAlert, ssParams });
            dispatch({
                meta: analytics,
                payload: {
                    id: payload,
                    key: savedSearchKey,
                },
                type: SAVE_SEARCH_SUCCESS,
            });
            dispatch(fetchSavedSearchesIfNeeded(true));
        } catch (error) {
            dispatch({
                error: true,
                meta: { key: createSaveSearchKeyBySearchParams(ssParams) },
                payload: error,
                type: SAVE_SEARCH_FAIL,
            });
        }
    };

export const deleteSavedSearch =
    (ssParams: SearchParams) => async (dispatch: Function, getState: () => GlobalState) => {
        try {
            const state = getState();
            const authToken = getAuthToken(state);
            const deployment = getDeployment(state);
            const saveSearchKey = createSaveSearchKeyBySearchParams(ssParams);
            const savedSearchId = getSavedSearchIdByKey(state, saveSearchKey);
            const analytics = getUnfollowSearchAnalytics();

            dispatch({
                payload: saveSearchKey,
                type: DELETE_SAVE_SEARCH_REQUEST,
            });

            await api.postDeleteSavedSearch({ authToken, deployment, savedSearchId });
            dispatch({
                meta: analytics,
                payload: saveSearchKey,
                type: DELETE_SAVE_SEARCH_SUCCESS,
            });
        } catch (error) {
            dispatch({
                error: true,
                payload: error,
                type: DELETE_SAVE_SEARCH_FAIL,
            });
        }
    };
